import api from "./index";

// ===== Inspections/OFI Management =====

// Get all inspections with optional filters
export const getInspections = (params = {}) =>
  api.get("property-management/inspections/", { params });

// Get a single inspection by ID
export const getInspection = (id) =>
  api.get(`property-management/inspections/${id}/`);

// Create a new inspection
export const createInspection = (data) =>
  api.post("property-management/inspections/", data);

// Update an inspection (full update)
export const updateInspection = (id, data) =>
  api.put(`property-management/inspections/${id}/`, data);

// Partially update an inspection
export const patchInspection = (id, data) =>
  api.patch(`property-management/inspections/${id}/`, data);

// Delete an inspection
export const deleteInspection = (id) =>
  api.delete(`property-management/inspections/${id}/`);


// ===== Inspection Attendees/Check-in =====

// Check-in to an inspection by numeric id (agent CRM — works for past sessions too)
export const checkInToInspection = (inspectionId, data) =>
  api.post(`property-management/inspections/${inspectionId}/checkin/`, data);

/**
 * POST a check-in to the public open-home token route, signed as the logged-in
 * agent. The endpoint itself is public (the QR page uses it too), but sending
 * the agent's credentials lets the backend record WHO entered the buyer's
 * details and auto-note it on the contact. Cookies cover the desktop case; the
 * Bearer token covers iOS/Safari, which blocks cross-site cookies on XHR.
 *
 * If the agent's token has expired the authenticated attempt 401s on an
 * otherwise-public endpoint. This posts with raw fetch, so it never passes
 * through the axios interceptor that refreshes the token — a stale
 * `jwt_access` (an open-home page left open past expiry is the normal case,
 * not the edge case) would silently downgrade the check-in to anonymous and
 * lose the "who entered these details" stamp for good. So refresh once and
 * retry signed, and only fall back to anonymous if the refresh also fails:
 * losing the attribution beats losing the buyer's details.
 */
const refreshAccessToken = async () => {
  const base = api.defaults.baseURL.replace(/\/$/, '');
  try {
    const res = await fetch(`${base}/api/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const payload = await res.json();
    const access = payload?.access || null;
    if (access && typeof localStorage !== 'undefined') {
      localStorage.setItem('jwt_access', access);
    }
    return access;
  } catch {
    return null;
  }
};

export const postSessionCheckin = async (checkinToken, data) => {
  const base = api.defaults.baseURL.replace(/\/$/, '');
  const url = `${base}/api/oh/${checkinToken}/checkin/`;
  const body = JSON.stringify(data);
  const jwt = (typeof localStorage !== 'undefined') ? localStorage.getItem('jwt_access') : null;

  const headers = { 'Content-Type': 'application/json' };
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  let res = await fetch(url, { method: 'POST', headers, body, credentials: 'include' });

  if (res.status === 401) {
    const fresh = await refreshAccessToken();
    if (fresh) {
      res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${fresh}` },
        body,
        credentials: 'include',
      });
    }
  }

  if (res.status === 401) {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
    });
  }
  return res;
};

/**
 * Log a buyer check-in from the CRM. Prefer the public token route during live
 * open homes — it matches the agent-device form on OpenHomeSessionPage and
 * avoids CSRF/cookie issues on mobile Safari.
 */
export const checkInBuyerToSession = async (inspection, data) => {
  const token = inspection?.checkin_token;
  if (token) {
    const res = await postSessionCheckin(token, data);
    if (res.ok) {
      return { data: await res.json() };
    }
    // Closed/cancelled sessions return 404 on the public route — agents can
    // still log late walk-ins via the CRM inspection id endpoint.
    if (res.status !== 404) {
      let payload = {};
      try {
        payload = await res.json();
      } catch {
        payload = { detail: res.statusText || 'Check-in failed' };
      }
      const err = new Error(payload.detail || payload.message || 'Check-in failed');
      err.response = { status: res.status, data: payload };
      throw err;
    }
  }
  return checkInToInspection(inspection.id, data);
};

// Get all attendees for an inspection
export const getInspectionAttendees = (inspectionId) =>
  api.get(`property-management/inspections/${inspectionId}/attendees/`);

// Register every attendee of this property against the property's suburb in the
// "Find Buyers for a Property" registry (adds the suburb to their buying
// requirements). Sweeps all opens for the property. → { updated, buyers, suburb }
export const syncAttendeePreferences = (inspectionId) =>
  api.post(`property-management/inspections/${inspectionId}/attendees/sync-preferences/`);

// Typeahead: find existing CRM contacts to re-add as an attendee (so the agent
// doesn't re-key details). Each result carries times_viewed for THIS property.
export const searchInspectionBuyers = (inspectionId, q) =>
  api.get(`property-management/inspections/${inspectionId}/buyer-search/`, { params: { q } });

// Get a single attendee by ID
export const getAttendee = (id) =>
  api.get(`property-management/attendees/${id}/`);

// Update an attendee
export const updateAttendee = (id, data) =>
  api.put(`property-management/attendees/${id}/`, data);

// Partially update an attendee
export const patchAttendee = (id, data) =>
  api.patch(`property-management/attendees/${id}/`, data);

// Delete an attendee
export const deleteAttendee = (id) =>
  api.delete(`property-management/attendees/${id}/`);

// Convert attendee to lead
export const convertAttendeeToLead = (id) =>
  api.post(`property-management/attendees/${id}/convert-to-lead/`);

// ===== Attendee buyer notes =====
// One shared thread per buyer: the Open Home tab and the property's Attendees
// tab read and write the exact same notes here, each stamped with its author
// and the time it was written. The backend stores them as ContactNotes on the
// buyer's contact (creating/linking one if the walk-in didn't have one yet) and
// folds any pre-existing attendee notes/feedback in on first read.
// Returns { contact_id, contact_name, notes: [...], last_contacted, entered_by }.
export const getAttendeeNotes = (id) =>
  api.get(`property-management/attendees/${id}/notes/`);

export const addAttendeeNote = (id, text) =>
  api.post(`property-management/attendees/${id}/notes/`, { text });

export const deleteAttendeeNote = (id, noteId) =>
  api.delete(`property-management/attendees/${id}/notes/${noteId}/`);

// Mark inspection as completed
export const completeInspection = (id, data = {}) =>
  api.post(`property-management/inspections/${id}/complete/`, data);

// Get inspection analytics
export const getInspectionAnalytics = (params = {}) =>
  api.get("property-management/inspections/analytics/", { params });


// ===== Buyer-submitted Inspection Requests =====

// List inspection requests (buyer-portal submissions awaiting/already actioned)
export const getInspectionRequests = (params = {}) =>
  api.get("property-management/inspection-requests/", { params });

// Confirm a pending request — optionally creates an OFI from the proposed time
export const confirmInspectionRequest = (id, data = {}) =>
  api.post(`property-management/inspection-requests/${id}/confirm/`, data);

// Cancel a request
export const cancelInspectionRequest = (id) =>
  api.post(`property-management/inspection-requests/${id}/cancel/`);


// ===== Open Home Check-in Flow =====

// Create an open home session (sale method + documents) — same as createInspection
// but with the extended payload. Keeps callers explicit about which flow they're on.
export const createOpenHome = (data) =>
  api.post("property-management/inspections/", data);

// Patch a session's qualification-level fields (sale method, document set, etc.)
export const updateOpenHome = (id, data) =>
  api.patch(`property-management/inspections/${id}/`, data);

// Close an open home session — locks the agent UI but keeps the check-in URL
// live for 24 hours afterwards for late doc requests.
export const closeOpenHome = (id) =>
  api.post(`property-management/inspections/${id}/close/`);

// Bulk send the post-visit SMS to every attendee on a session.
// payload (all optional): { template, attendee_ids, resend }
export const sendPostVisitSms = (id, payload = {}) =>
  api.post(`property-management/inspections/${id}/send-post-visit-sms/`, payload);

// Render per-attendee post-visit SMS bodies for the agent's OWN device to send
// natively (text comes from the agent's personal number). Does not send anything.
// payload (all optional): { template, attendee_ids, include_sent }
// → { messages: [{ attendee_id, name, phone, body, already_sent }], skipped, agent_name, address, next_oh }
export const getBulkSmsPayload = (id, payload = {}) =>
  api.post(`property-management/inspections/${id}/bulk-sms-payload/`, payload);

// Flag attendees as messaged from the agent's device (best-effort, after the
// agent works through the native Messages queue). payload: { attendee_ids }
export const markSmsHandedOff = (id, attendeeIds) =>
  api.post(`property-management/inspections/${id}/mark-sms-handed-off/`, { attendee_ids: attendeeIds });

// Send the property's Contract of Sale to every attendee on a session.
// Returns 400 if no contract document is attached to the property.
// payload (all optional): { template, attendee_ids }
export const sendContractOfSale = (id, payload = {}) =>
  api.post(`property-management/inspections/${id}/send-contract-of-sale/`, payload);

// Save the agent's per-buyer qualification fields (interest, finance, settlement etc).
export const qualifyAttendee = (id, fields) =>
  api.patch(`property-management/attendees/${id}/qualify/`, fields);

// Promote a checked-in buyer straight to a Hot Lead.
export const promoteAttendeeToLead = (id) =>
  api.post(`property-management/attendees/${id}/promote-to-lead/`);

// Buyer activity timeline — used on the Contact profile "Inspections" tab.
export const getContactActivityLog = (contactId) =>
  api.get(`property-management/contacts/${contactId}/activity-log/`);

export const addContactActivity = (contactId, payload) =>
  api.post(`property-management/contacts/${contactId}/activity-log/add/`, payload);


